import QRCode from 'qrcode';

export const QR_OPTIONS = {
  errorCorrectionLevel: 'H',
  margin: 4,
  color: {
    dark: '#000000',
    light: '#FFFFFFFF',
  },
};

const LOGO_URL = '/megachat-icon.png';
const BADGE_SIZE_RATIO = 0.16;
const BADGE_CORNER_RADIUS_RATIO = 0.025;
const LOGO_SIZE_RATIO = BADGE_SIZE_RATIO * 0.48;

const formatNumber = value =>
  Number.isInteger(value) ? String(value) : String(Number(value.toFixed(3)));

const toDataUrl = blob =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

const loadImage = source =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = source;
  });

export const loadMegaChatLogo = async () => {
  const response = await fetch(LOGO_URL);
  if (!response.ok) throw new Error('Unable to load the MegaChat QR Code logo');

  return toDataUrl(await response.blob());
};

const svgLogoOverlay = (svg, logoDataUrl) => {
  const viewBox = svg.match(/viewBox=["']([^"']+)["']/i)?.[1];
  if (!viewBox) throw new Error('QR Code SVG must include a viewBox');

  const [minX, minY, width, height] = viewBox.split(/\s+/).map(Number);
  const size = Math.min(width, height);
  const centerX = minX + width / 2;
  const centerY = minY + height / 2;
  const badgeSize = size * BADGE_SIZE_RATIO;
  const badgePositionX = centerX - badgeSize / 2;
  const badgePositionY = centerY - badgeSize / 2;
  const badgeRadius = size * BADGE_CORNER_RADIUS_RATIO;
  const logoSize = size * LOGO_SIZE_RATIO;
  const logoX = centerX - logoSize / 2;
  const logoY = centerY - logoSize / 2;
  const overlay = `<rect x="${formatNumber(badgePositionX)}" y="${formatNumber(badgePositionY)}" width="${formatNumber(badgeSize)}" height="${formatNumber(badgeSize)}" rx="${formatNumber(badgeRadius)}" fill="#FFFFFF" stroke="#E2E8F0" stroke-width="0.35"/><image href="${logoDataUrl}" x="${formatNumber(logoX)}" y="${formatNumber(logoY)}" width="${formatNumber(logoSize)}" height="${formatNumber(logoSize)}"/>`;

  return svg.replace('</svg>', `${overlay}</svg>`);
};

export const createBrandedSvg = async (value, logoDataUrl = null) => {
  const [svg, logo] = await Promise.all([
    QRCode.toString(value, { ...QR_OPTIONS, type: 'svg' }),
    logoDataUrl ? Promise.resolve(logoDataUrl) : loadMegaChatLogo(),
  ]);

  return svgLogoOverlay(svg, logo);
};

const drawBadge = (context, width) => {
  const badgeSize = width * BADGE_SIZE_RATIO;
  const badgePosition = (width - badgeSize) / 2;
  const badgeRadius = width * BADGE_CORNER_RADIUS_RATIO;

  context.beginPath();
  context.roundRect(
    badgePosition,
    badgePosition,
    badgeSize,
    badgeSize,
    badgeRadius
  );
  context.fillStyle = '#FFFFFF';
  context.fill();
  context.strokeStyle = '#E2E8F0';
  context.lineWidth = Math.max(1, width * 0.001);
  context.stroke();
};

export const createBrandedPng = async (value, width) => {
  const [qrCodeDataUrl, logoDataUrl] = await Promise.all([
    QRCode.toDataURL(value, { ...QR_OPTIONS, width }),
    loadMegaChatLogo(),
  ]);
  const [qrCodeImage, logoImage] = await Promise.all([
    loadImage(qrCodeDataUrl),
    loadImage(logoDataUrl),
  ]);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = width;
  const context = canvas.getContext('2d');
  const logoSize = width * LOGO_SIZE_RATIO;
  const logoPosition = (width - logoSize) / 2;

  context.fillStyle = '#FFFFFF';
  context.fillRect(0, 0, width, width);
  context.imageSmoothingEnabled = false;
  context.drawImage(qrCodeImage, 0, 0, width, width);
  drawBadge(context, width);
  context.drawImage(logoImage, logoPosition, logoPosition, logoSize, logoSize);

  return canvas.toDataURL('image/png');
};
